import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase/client';
import { cn } from '@/lib/utils';
import { localizedPath, stripLocaleFromPath } from '@/lib/i18n';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import {
  LayoutDashboard,
  Wallet,
  Users,
  Target,
  Sparkles,
  FileText,
  Calculator,
  Download,
  Settings,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  CheckCircle2,
  StickyNote,
  Brain,
  Heart,
  Building2,
  UserSearch,
  UsersRound,
  Briefcase,
  GraduationCap,
  Ticket,
  MessageSquare,
  Crosshair,
  Mail,
  HelpCircle,
  Shield,
  DollarSign,
  Crown,
  Ban,
  BarChart3,
  Scale,
  Gift,
  Palette,
  Trophy,
  Globe,
  FileBarChart,
  MailX,
  Rocket,
  TrendingUp,
  FileDiff,
  FolderOpen,
  ClipboardCheck,
  MoreHorizontal,
  Calendar,
  Cloud,
  Home,
  ListChecks,
} from 'lucide-react';
import { useSubscription, type Tier } from '@/hooks/useSubscription';
import { DisclosureStatusBadge } from '@/components/disclosure/DisclosureStatusBadge';
import { useHHDPilot } from '@/hooks/useFeatureFlags';
import { useFirmManagedCase } from '@/hooks/useFirmManagedCase';
import { useResolvedFeatures } from '@/hooks/useFeature';
import { useAdvisorProfile } from '@/hooks/useAdvisorProfile';
import { hasReferralAccess } from '@/lib/advisorExperience';
import { buildClientNav, type ClientNavItem, type NavIconKey } from './navConfig';
import { useExperiencePreset } from '@/hooks/useFeature';
import { useBranding } from '@/contexts/BrandingContext';
import posthog from 'posthog-js';
import { trackEvent } from '@/lib/posthog';
import { upsellEvents } from '@/lib/analytics/events/upsell';

interface NavItem {
  label: string;
  href: string;
  icon: React.ElementType;
  badge?: 'complete' | 'count' | 'new' | 'beta';
  badgeValue?: number;
  requiredTier?: Tier;
  dataTour?: string;
  featureFlag?: string;
  customBadge?: React.ReactNode;
}

interface NavSection {
  title?: string;
  icon?: React.ElementType;
  items: NavItem[];
}

interface SidebarProps {
  collapsed: boolean;
  onToggle: () => void;
  onNavigate?: () => void;
}

export function Sidebar({ collapsed, onToggle, onNavigate }: SidebarProps) {
  const location = useLocation();
  const pathname = location.pathname;
  const navigate = useNavigate();
  const { user, profile } = useAuth();
  const { t } = useTranslation('navigation');
  const { canAccessTier, tier } = useSubscription();
  const { branding, isWhiteLabeled } = useBranding();
  const { enabled: isHHDPilot } = useHHDPilot();
  const { isFirmManaged, isLoading: firmManagedLoading } = useFirmManagedCase();
  const { data: advisorProfile } = useAdvisorProfile();
  // Feature registry (B2B Phase A): hide nav items whose feature resolves off
  // for the current experience preset (firm vs individual). Routes with no
  // registered feature are unaffected.
  const { routeEnabled: featureRouteEnabled } = useResolvedFeatures();

  // #1516: every firm-managed client gets the compact, workflow-first nav on
  // every route. While the advisor-connection query is in flight we must not
  // flash the legacy DIY nav at a firm client, so the client nav waits.
  const experiencePreset = useExperiencePreset();
  // navConfig is React/i18next-free: hand it a plain (key, fallback) → string translator.
  const navT = (key: string, fallback?: string): string => String(t(key, { defaultValue: fallback ?? key }));
  const clientNav = buildClientNav({ isHHDPilot, isFirmManaged, t: navT, preset: experiencePreset });

  const [hasStrategy, setHasStrategy] = useState(false);
  const [childrenCount, setChildrenCount] = useState(0);
  const [documentCount, setDocumentCount] = useState(0);
  const [reviewQueueCount, setReviewQueueCount] = useState(0);
  const [moreExpanded, setMoreExpanded] = useState(false);

  // Check if user is an advisor
  const isAdvisorUser = profile?.is_advisor === true;

  // Check if user is an admin
  const isAdminUser = profile?.role === 'admin';

  // Strip locale from pathname for route detection
  const cleanPathname = stripLocaleFromPath(pathname);

  // Check if currently on admin routes
  const isOnAdminRoute = cleanPathname.startsWith('/admin');

  // Fetch completion status (only for non-advisors)
  useEffect(() => {
    if (!user || isAdvisorUser) return;

    const fetchStatus = async () => {
      const [strategyRes, childrenRes, docsRes, reviewAssetsRes, reviewDebtsRes, reviewIncomeRes] = await Promise.all([
        supabase
          .from('strategies')
          .select('id')
          .eq('user_id', user.id)
          .eq('status', 'complete')
          .limit(1),
        supabase
          .from('children')
          .select('id')
          .eq('user_id', user.id),
        supabase
          .from('uploaded_documents')
          .select('id')
          .eq('user_id', user.id),
        // Count items needing review
        supabase
          .from('assets')
          .select('id', { count: 'exact', head: true })
          .eq('user_id', user.id)
          .eq('needs_review', true),
        supabase
          .from('debts')
          .select('id', { count: 'exact', head: true })
          .eq('user_id', user.id)
          .eq('needs_review', true),
        supabase
          .from('income_sources')
          .select('id', { count: 'exact', head: true })
          .eq('user_id', user.id)
          .eq('needs_review', true),
      ]);

      setHasStrategy((strategyRes.data?.length || 0) > 0);
      setChildrenCount(childrenRes.data?.length || 0);
      setDocumentCount(docsRes.data?.length || 0);
      setReviewQueueCount(
        (reviewAssetsRes.count || 0) +
        (reviewDebtsRes.count || 0) +
        (reviewIncomeRes.count || 0)
      );
    };

    fetchStatus();
  }, [user, isAdvisorUser, supabase]);

  const mainNav: NavSection = {
    items: [
      { label: t('sidebar.dashboard'), href: '/dashboard', icon: LayoutDashboard },
      { label: t('sidebar.learn'), href: '/learn', icon: GraduationCap, badge: 'new' as const },
      {
        label: t('sidebar.financials', 'Financials'),
        href: '/financials',
        icon: Wallet,
        badge: reviewQueueCount > 0 ? 'count' : (profile?.intake_complete ? 'complete' : undefined),
        badgeValue: reviewQueueCount > 0 ? reviewQueueCount : undefined,
        dataTour: 'net-worth',
      },
      {
        label: t('sidebar.children'),
        href: '/children',
        icon: Users,
        badge: childrenCount > 0 ? 'count' : undefined,
        badgeValue: childrenCount,
      },
      {
        label: t('sidebar.goals'),
        href: '/goals',
        icon: Target,
        badge: profile?.goals_complete ? 'complete' : undefined,
      },
      {
        label: t('sidebar.strategy'),
        href: '/strategy',
        icon: Sparkles,
        badge: hasStrategy ? 'complete' : undefined,
        requiredTier: 'core',
        dataTour: 'strategy',
      },
      { label: t('sidebar.correspondence'), href: '/correspondence', icon: Mail, badge: 'beta' as const },
      { label: t('sidebar.myTeam'), href: '/my-team', icon: UsersRound },
    ],
  };

  const intelligenceNav: NavSection = {
    title: t('sidebar.sections.intelligence'),
    icon: Brain,
    items: [
      { label: t('sidebar.askSage'), href: '/ask-sage', icon: MessageSquare, dataTour: 'ask-sage' },
      { label: t('sidebar.sibyl', 'Sibyl'), href: '/sibyl', icon: Crosshair, badge: 'beta' as const, requiredTier: 'premium' },
      { label: t('sidebar.caseIntelligence'), href: '/case-intelligence', icon: Brain, requiredTier: 'premium' },
      { label: t('sidebar.knowYourSpouse'), href: '/know-your-spouse', icon: UserSearch, requiredTier: 'premium' },
      { label: t('sidebar.assetEmotions'), href: '/asset-emotions', icon: Heart, requiredTier: 'premium' },
      { label: t('sidebar.settlementWizard', 'Settlement Wizard'), href: '/settlement-wizard', icon: Scale, badge: 'beta' as const, requiredTier: 'premium' },
      { label: t('sidebar.redlines', 'Redlines'), href: '/redline', icon: FileDiff, badge: 'beta' as const, requiredTier: 'premium' },
      { label: t('sidebar.entityPlanning'), href: '/entity-planning', icon: Building2, requiredTier: 'premium' },
    ],
  };

  const toolsNav: NavSection = {
    title: t('sidebar.sections.tools'),
    items: [
      {
        label: isHHDPilot ? 'Disclosure' : 'Case Library',
        href: '/case-library',
        icon: isHHDPilot ? ClipboardCheck : FolderOpen,
        badge: isHHDPilot ? undefined : ('beta' as const),
        customBadge: isHHDPilot ? <DisclosureStatusBadge /> : undefined,
      },
      {
        label: t('sidebar.documents'),
        href: '/documents',
        icon: FileText,
        badge: documentCount > 0 ? 'count' : undefined,
        badgeValue: documentCount,
        requiredTier: 'core',
        dataTour: 'documents',
      },
      ...(isHHDPilot ? [{
        label: 'Discovery',
        href: '/discovery',
        icon: FileBarChart,
      }] : []),
      { label: t('sidebar.notes'), href: '/notes', icon: StickyNote },
      { label: t('sidebar.supportCalculator'), href: '/support-calculator', icon: Calculator, requiredTier: 'core', dataTour: 'support-calc' },
      { label: t('sidebar.export'), href: '/export', icon: Download, requiredTier: 'core' },
    ],
  };

  const footerNav: NavSection = {
    items: [
      { label: t('sidebar.help'), href: '/guides', icon: HelpCircle },
      // The collector preset already lists Settings as one of its three primary items (#1747).
      ...(experiencePreset === 'collector' ? [] : [{ label: t('sidebar.settings'), href: '/settings', icon: Settings }]),
    ],
  };

  // Advisor navigation. Referrals & Earnings is hidden for legal professions
  // (#1513): lawyer referral compensation implicates bar fee-splitting rules.
  const advisorMainNav: NavSection = {
    items: [
      { label: t('sidebar.advisor.myClients'), href: '/advisor', icon: Briefcase },
      { label: t('sidebar.askSage'), href: '/advisor/ask-sage', icon: MessageSquare },
      { label: t('sidebar.advisor.team'), href: '/advisor/team', icon: UsersRound },
      { label: t('sidebar.advisor.privilege', 'Privilege Docs'), href: '/advisor/settings/privilege', icon: Shield },
      { label: t('sidebar.advisor.billing'), href: '/advisor/settings/billing', icon: Ticket },
      { label: 'Branding', href: '/advisor/settings/branding', icon: Palette },
      { label: 'Integrations', href: '/advisor/settings/integrations', icon: Cloud },
      ...(hasReferralAccess(advisorProfile?.profession_type)
        ? [{ label: 'Referrals & Earnings', href: '/advisor/referrals', icon: Gift }]
        : []),
      { label: 'Launch Checklist', href: '/advisor/settings/launch-checklist', icon: Rocket },
    ],
  };

  // Admin navigation (shown when on admin routes)
  const adminMainNav: NavSection = {
    items: [
      { label: 'Overview', href: '/admin', icon: Shield },
      { label: 'Users', href: '/admin/users', icon: Users },
      { label: 'Banned Emails', href: '/admin/banned-emails', icon: Ban },
      { label: 'Profitability', href: '/admin/profitability', icon: DollarSign },
      { label: 'Growth Intelligence', href: '/admin/growth-intelligence', icon: BarChart3 },
      { label: 'Conversion Intelligence', href: '/admin/conversion-intelligence', icon: TrendingUp },
      { label: 'Partners', href: '/admin/partners', icon: Briefcase },
      { label: 'Leaderboard', href: '/admin/partners/leaderboard', icon: Trophy },
      { label: 'Portals', href: '/admin/portals', icon: Globe },
      { label: 'Prospects', href: '/admin/prospects', icon: UserSearch },
      { label: 'Commissions', href: '/admin/commissions', icon: Wallet },
      { label: 'Cohort Analysis', href: '/admin/analytics/cohorts', icon: BarChart3 },
      { label: 'Reports', href: '/admin/reports', icon: FileBarChart },
      { label: 'Opt-Outs', href: '/admin/opt-outs', icon: MailX },
      { label: 'Demo Requests', href: '/admin/demo-requests', icon: Calendar },
      { label: 'Campaigns', href: '/admin/campaigns', icon: Mail },
    ],
  };

  // Footer nav for admin users (when NOT on admin routes) - includes Admin Dashboard link
  const adminUserFooterNav: NavSection = {
    items: [
      { label: t('sidebar.help'), href: '/guides', icon: HelpCircle },
      { label: 'Admin Dashboard', href: '/admin', icon: Shield },
      { label: t('sidebar.settings'), href: '/settings', icon: Settings },
    ],
  };

  // Admin footer nav (shown when on admin routes)
  const adminFooterNav: NavSection = {
    items: [
      { label: 'Back to App', href: '/dashboard', icon: ChevronLeft },
      { label: t('sidebar.settings'), href: '/settings', icon: Settings },
    ],
  };

  // #1323: map compact-nav iconKeys → lucide components.
  const NAV_ICONS: Record<NavIconKey, React.ElementType> = {
    home: Home,
    tasks: ListChecks,
    disclosure: ClipboardCheck,
    documents: FileText,
    discovery: FileBarChart,
    team: UsersRound,
    financials: Wallet,
    strategy: Sparkles,
    support: Calculator,
    export: Download,
    intelligence: Brain,
    askSage: MessageSquare,
    notes: StickyNote,
    help: HelpCircle,
    settings: Settings,
  };

  const renderCompactNavItem = (item: ClientNavItem, primary: boolean) => {
    if (!featureRouteEnabled(item.href)) return null;
    const isActive = stripLocaleFromPath(pathname) === item.href;
    const Icon = NAV_ICONS[item.iconKey];
    const link = (
      <Link
        to={localizedPath(item.href)}
        onClick={() => onNavigate?.()}
        data-testid={primary ? 'nav-item-primary' : 'nav-item-more'}
        className={cn(
          'flex items-center gap-3 px-3 py-2 min-h-[44px] rounded-lg transition-colors group',
          'hover:bg-slate-100 active:bg-slate-200',
          isActive && 'bg-primary/10 text-primary font-medium hover:bg-primary/15 active:bg-primary/20',
          !isActive && 'text-slate-600',
          collapsed && 'justify-center px-2'
        )}
      >
        <Icon className={cn('h-5 w-5 shrink-0', isActive && 'text-primary')} />
        {!collapsed && (
          <span className="text-sm overflow-hidden text-ellipsis whitespace-nowrap flex-1 min-w-0">
            {item.label}
          </span>
        )}
      </Link>
    );

    if (collapsed) {
      return (
        <Tooltip key={item.href} delayDuration={0}>
          <TooltipTrigger asChild>{link}</TooltipTrigger>
          <TooltipContent side="right">{item.label}</TooltipContent>
        </Tooltip>
      );
    }
    return <div key={item.href}>{link}</div>;
  };

  const renderNavItem = (item: NavItem) => {
    // Feature-registry gate: hide items whose feature is off for this preset.
    if (!featureRouteEnabled(item.href)) return null;
    // Strip locale from pathname for comparison (e.g., /es/dashboard -> /dashboard)
    const cleanPathname = stripLocaleFromPath(pathname);
    const isActive = cleanPathname === item.href;
    const Icon = item.icon;

    // Check if user has access to this tier - show crown for users who don't have access
    const hasAccess = !item.requiredTier || canAccessTier(item.requiredTier);
    const showPremiumBadge = !!item.requiredTier && !hasAccess;

    const handleNavClick = () => {
      if (showPremiumBadge) {
        const event = upsellEvents.premiumTeaserClicked(item.href, 'sidebar');
        trackEvent(event.event, event.properties);
      }
      onNavigate?.();
    };

    const content = (
      <Link
        to={localizedPath(item.href)}
        onClick={handleNavClick}
        data-tour={item.dataTour}
        className={cn(
          'flex items-center gap-3 px-3 py-2 min-h-[44px] rounded-lg transition-colors group',
          'hover:bg-slate-100 active:bg-slate-200',
          isActive && 'bg-primary/10 text-primary font-medium hover:bg-primary/15 active:bg-primary/20',
          !isActive && 'text-slate-600',
          collapsed && 'justify-center px-2'
        )}
        style={{ maxWidth: collapsed ? undefined : '100%' }}
      >
        <Icon className={cn('h-5 w-5 shrink-0', isActive && 'text-primary')} />
        {!collapsed && (
          <>
            <span
              className="text-sm overflow-hidden text-ellipsis whitespace-nowrap"
              style={{ flex: '1 1 0%', minWidth: 0 }}
            >
              {item.label}
            </span>
            {item.badge === 'complete' && (
              <CheckCircle2 className="h-4 w-4 text-emerald-500 shrink-0" />
            )}
            {item.badge === 'count' && item.badgeValue && item.badgeValue > 0 && (
              <span className="text-xs bg-slate-200 text-slate-600 px-1.5 py-0.5 rounded-full shrink-0">
                {item.badgeValue}
              </span>
            )}
            {item.badge === 'new' && (
              <span className="text-[10px] bg-primary text-white px-1.5 py-0.5 rounded-full font-medium shrink-0 whitespace-nowrap">
                {t('sidebar.badges.new')}
              </span>
            )}
            {item.badge === 'beta' && (
              <span className="text-[10px] bg-amber-500 text-white px-1.5 py-0.5 rounded-full font-medium shrink-0 whitespace-nowrap">
                {t('sidebar.badges.beta')}
              </span>
            )}
            {item.customBadge}
            {showPremiumBadge && (
              <Tooltip delayDuration={0}>
                <TooltipTrigger asChild>
                  <Crown className="h-3.5 w-3.5 text-amber-500 shrink-0" />
                </TooltipTrigger>
                <TooltipContent side="right">
                  <p className="text-xs">
                    {item.requiredTier === 'premium'
                      ? t('sidebar.badges.premiumFeature', 'Premium feature')
                      : t('sidebar.badges.paidFeature', 'Upgrade to access')}
                  </p>
                </TooltipContent>
              </Tooltip>
            )}
          </>
        )}
      </Link>
    );

    if (collapsed) {
      return (
        <Tooltip key={item.href} delayDuration={0}>
          <TooltipTrigger asChild>{content}</TooltipTrigger>
          <TooltipContent side="right" className="flex items-center gap-2">
            {item.label}
            {item.badge === 'complete' && (
              <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500" />
            )}
            {item.badge === 'count' && item.badgeValue && item.badgeValue > 0 && (
              <span className="text-xs">({item.badgeValue})</span>
            )}
            {item.badge === 'new' && (
              <span className="text-[10px] bg-primary text-white px-1.5 py-0.5 rounded-full font-medium ml-1">
                {t('sidebar.badges.new')}
              </span>
            )}
            {item.badge === 'beta' && (
              <span className="text-[10px] bg-amber-500 text-white px-1.5 py-0.5 rounded-full font-medium ml-1">
                {t('sidebar.badges.beta')}
              </span>
            )}
            {showPremiumBadge && (
              <Crown className="h-3.5 w-3.5 text-amber-500 ml-1" />
            )}
          </TooltipContent>
        </Tooltip>
      );
    }

    return <div key={item.href}>{content}</div>;
  };

  const renderSection = (section: NavSection, showDivider = false) => (
    <div key={section.title || 'main'} className="w-full overflow-hidden">
      {showDivider && <div className="h-px bg-slate-200 my-3 mx-3" />}
      {section.title && !collapsed && (
        <div className="flex items-center gap-2 px-3 mb-2 w-full">
          {section.icon && <section.icon className="h-3.5 w-3.5 text-slate-400 flex-shrink-0" />}
          <p className="text-xs font-medium text-slate-400 uppercase tracking-wider truncate flex-1 min-w-0">
            {section.title}
          </p>
        </div>
      )}
      <div className="space-y-1 w-full">{section.items.filter(item => {
        if (!item.featureFlag) return true;
        // Check PostHog flag with fallback: also allow if user email matches
        const flagEnabled = posthog?.isFeatureEnabled?.(item.featureFlag);
        const emailOverride = profile?.email === 'scott+diiivorce@kodey.ai' || profile?.email === 'sfielder@trifinlabs.com';
        return flagEnabled || emailOverride;
      }).map(renderNavItem)}</div>
    </div>
  );

  return (
    <aside
      className={cn(
        'relative flex flex-col h-full bg-white border-r border-slate-200 transition-all duration-300 flex-shrink-0',
        collapsed ? 'w-16' : 'w-64'
      )}
      style={{ overflow: 'visible' }}
    >
      {/* Logo */}
      <div
        className={cn(
          'flex items-center h-16 border-b border-slate-200 px-4 overflow-hidden',
          collapsed && 'justify-center px-2'
        )}
      >
        <Link to={localizedPath('/dashboard')} className="flex items-center overflow-hidden">
          {isWhiteLabeled && branding.logo_url ? (
            collapsed ? (
              branding.favicon_url ? (
                <img
                  src={branding.favicon_url}
                  alt={branding.company_name}
                  className="w-8 h-8 flex-shrink-0 rounded"
                />
              ) : (
                <span className="w-8 h-8 flex-shrink-0 rounded bg-primary/10 text-primary font-bold text-lg flex items-center justify-center">
                  {branding.company_name.charAt(0)}
                </span>
              )
            ) : (
              <img
                src={branding.logo_url}
                alt={branding.company_name}
                className="h-8 w-auto flex-shrink-0"
              />
            )
          ) : collapsed ? (
            <img
              src="/settle-wise-favicon.png"
              alt="SettleWise"
              className="w-8 h-8 flex-shrink-0"
            />
          ) : (
            <img
              src="/settlewise-logo.png"
              alt="SettleWise"
              className="h-8 w-auto flex-shrink-0"
            />
          )}
        </Link>
      </div>

      {/* Navigation */}
      <ScrollArea className="flex-1 py-4">
        <nav className="px-2 space-y-1 overflow-hidden">
          {isAdminUser && isOnAdminRoute ? (
            // Admin users on admin routes see only admin navigation
            <>{renderSection(adminMainNav)}</>
          ) : isAdvisorUser && cleanPathname.startsWith('/advisor') ? (
            // Advisor nav only when actually on advisor routes
            <>{renderSection(advisorMainNav)}</>
          ) : firmManagedLoading ? (
            // #1516: don't flash the legacy DIY nav while we determine whether
            // this client is firm-managed — render a neutral skeleton instead.
            <div className="space-y-2 px-3 py-1" data-testid="client-nav-loading">
              {Array.from({ length: 5 }).map((_, i) => (
                <div key={i} className="h-9 rounded-lg bg-slate-100 animate-pulse" />
              ))}
            </div>
          ) : (
            // Client nav for all other routes (including dual-account users on /dashboard)
            clientNav.mode === 'compact' ? (
              // #1323: firm-managed pilot client — compact, workflow-first nav.
              <div data-testid="firm-client-nav" className="space-y-1 w-full">
                <div className="space-y-1 w-full">
                  {clientNav.primary.map((item) => renderCompactNavItem(item, true))}
                </div>
                {clientNav.more.length > 0 && (
                <Collapsible open={moreExpanded} onOpenChange={setMoreExpanded}>
                  <div className="h-px bg-slate-200 my-3 mx-3" />
                  <CollapsibleTrigger asChild>
                    <button
                      className={cn(
                        'flex items-center gap-3 px-3 py-2 min-h-[44px] rounded-lg transition-colors w-full',
                        'hover:bg-slate-100 active:bg-slate-200 text-slate-500',
                        collapsed && 'justify-center px-2'
                      )}
                      data-testid="nav-more-group"
                    >
                      <MoreHorizontal className="h-5 w-5 shrink-0" />
                      {!collapsed && (
                        <>
                          <span className="text-sm flex-1 text-left">{t('sidebar.more', 'More')}</span>
                          <ChevronDown
                            className={cn(
                              'h-4 w-4 shrink-0 transition-transform',
                              moreExpanded && 'rotate-180'
                            )}
                          />
                        </>
                      )}
                    </button>
                  </CollapsibleTrigger>
                  <CollapsibleContent>
                    <div className="space-y-1 w-full">
                      {clientNav.more.map((item) => renderCompactNavItem(item, false))}
                    </div>
                  </CollapsibleContent>
                </Collapsible>
                )}
              </div>
            ) : (
            (() => {
              // HHD Pilot: Progressive sidebar disclosure
              // Phase 1 (collecting documents): 5 essential items only
              // Phase 2 (disclosure >= 90% OR has strategy): Unlock more tools
              // Phase 3 (has strategy): Full intelligence suite
              const isProgressiveMode = isHHDPilot;

              // Phase 1: Document collection essentials
              const phase1Hrefs = new Set([
                '/dashboard',
                '/case-library',
                '/financials',
                '/discovery',
                '/ask-sage',
                '/settings',
              ]);

              // Phase 2: Strategy & export tools (unlocked post-disclosure or post-strategy)
              const phase2Hrefs = new Set([
                ...phase1Hrefs,
                '/strategy',
                '/correspondence',
                '/support-calculator',
                '/children',
                '/export',
                '/goals',
              ]);

              // Phase 3: Full intelligence suite (unlocked post-strategy)
              const phase3Hrefs = new Set([
                ...phase2Hrefs,
                '/case-intelligence',
                '/know-your-spouse',
                '/asset-emotions',
                '/settlement-wizard',
                '/redline',
                '/entity-planning',
                '/sibyl',
                '/documents',
                '/notes',
                '/my-team',
                '/learn',
              ]);

              // Determine active phase
              // HHD clients always get a curated nav — never the full 22-item blast
              // Phase 1: collecting documents (no strategy yet)
              // Phase 2: strategy exists — unlock more tools but keep it focused
              const activePhaseHrefs = hasStrategy
                ? phase2Hrefs
                : phase1Hrefs;

              const filterItems = (section: NavSection): NavSection => ({
                ...section,
                items: section.items.filter(item => activePhaseHrefs.has(item.href)),
              });

              const getHiddenItems = (section: NavSection): NavItem[] =>
                section.items.filter(item => !activePhaseHrefs.has(item.href));

              if (!isProgressiveMode) {
                return (
                  <>
                    {renderSection(mainNav)}
                    {renderSection(intelligenceNav, true)}
                    {renderSection(toolsNav, true)}
                  </>
                );
              }

              // Progressive mode: filter sections and collect hidden items
              const filteredMain = filterItems(mainNav);
              const filteredIntelligence = filterItems(intelligenceNav);
              const filteredTools = filterItems(toolsNav);

              const hiddenItems: NavItem[] = [
                ...getHiddenItems(mainNav),
                ...getHiddenItems(intelligenceNav),
                ...getHiddenItems(toolsNav),
              ];

              return (
                <>
                  {filteredMain.items.length > 0 && renderSection(filteredMain)}
                  {filteredIntelligence.items.length > 0 && renderSection(filteredIntelligence, true)}
                  {filteredTools.items.length > 0 && renderSection(filteredTools, true)}
                  {hiddenItems.length > 0 && (
                    <Collapsible open={moreExpanded} onOpenChange={setMoreExpanded}>
                      <div className="h-px bg-slate-200 my-3 mx-3" />
                      <CollapsibleTrigger asChild>
                        <button
                          className={cn(
                            'flex items-center gap-3 px-3 py-2 min-h-[44px] rounded-lg transition-colors w-full',
                            'hover:bg-slate-100 active:bg-slate-200 text-slate-500',
                            collapsed && 'justify-center px-2'
                          )}
                          data-testid="sidebar-more-trigger"
                        >
                          <MoreHorizontal className="h-5 w-5 shrink-0" />
                          {!collapsed && (
                            <>
                              <span className="text-sm flex-1 text-left">{t('sidebar.more', 'More')}</span>
                              <ChevronDown className={cn(
                                'h-4 w-4 shrink-0 transition-transform',
                                moreExpanded && 'rotate-180'
                              )} />
                            </>
                          )}
                        </button>
                      </CollapsibleTrigger>
                      <CollapsibleContent>
                        <div className="space-y-1 w-full" data-testid="sidebar-more-items">
                          {hiddenItems.filter(item => {
                            if (!item.featureFlag) return true;
                            const flagEnabled = posthog?.isFeatureEnabled?.(item.featureFlag);
                            const emailOverride = profile?.email === 'scott+diiivorce@kodey.ai' || profile?.email === 'sfielder@trifinlabs.com';
                            return flagEnabled || emailOverride;
                          }).map(renderNavItem)}
                        </div>
                      </CollapsibleContent>
                    </Collapsible>
                  )}
                </>
              );
            })()
          ))}
        </nav>
      </ScrollArea>

      {/* Footer */}
      <div className="border-t border-slate-200 p-2">
        {isAdminUser && isOnAdminRoute
          ? renderSection(adminFooterNav)
          : isAdminUser
            ? renderSection(adminUserFooterNav)
            : renderSection(footerNav)}
        {isWhiteLabeled && branding.show_powered_by && !collapsed && (
          <p className="text-[10px] text-slate-400 text-center mt-2 px-3">
            Powered by SettleWise
          </p>
        )}
      </div>

      {/* Collapse Toggle - positioned to overlap the border */}
      <button
        onClick={onToggle}
        className={cn(
          'absolute bottom-4 right-0 translate-x-1/2 z-50',
          'w-7 h-7 rounded-full',
          'bg-white border border-slate-200 shadow-md',
          'flex items-center justify-center',
          'text-slate-500 hover:text-slate-700 hover:bg-slate-50 hover:shadow-lg',
          'transition-all duration-200'
        )}
        aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
      >
        {collapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
      </button>
    </aside>
  );
}

export default Sidebar;
